import { Clock, Lock } from 'lucide-react';
import PlayerNewsIcon from '@/components/player/PlayerNewsIcon';
import { cn } from '@/lib/utils';
import { AvailabilityPill } from './PlayerAvailability';
import { gameLine, playerMetaLine, type GameContext } from '@/lib/nflGameLine';

/**
 * SAB-1003 / SAB-994: the shared player identity block for My Team and Matchup rows.
 *
 * ── WHY ONLY THIS PART IS SHARED ────────────────────────────────────────────
 *
 * Three implementations existed. My Team's RosterRow carries drag/drop, a badge-tap move
 * target and an IR/Drop cluster; the Matchup starter cell is a MIRRORED two-player
 * comparison with a centre slot toggle; the Matchup bench cell rendered a bare name. The
 * containers are genuinely different and forcing one component over all three would mean a
 * prop soup with an `isMatchup` branch through the middle.
 *
 * What IS identical is the identity: who the player is, what he plays, who he plays this
 * week, and whether he is available. That is extracted here so the three rows converge by
 * construction rather than by three people remembering. SAB-1003 asks for the row to be
 * redesigned once, not twice -- this is the seam that makes that possible.
 *
 * `align` exists because the Matchup home side is right-aligned; it flips text and the
 * order of the inline chips, and nothing else.
 *
 * ── THE PROJECTION IS NOT HERE ──────────────────────────────────────────────
 *
 * SAB-1009 briefly rendered the weekly projection on the meta line beside the game info.
 * It now lives in the matchup row's SCORE COLUMN, stacked under the score and centred on
 * it, because that is the only way the two can align: they sat in different containers,
 * and right-aligning a one-character dash against a four-character number aligns their
 * right edges rather than their centres. Do not put it back here without solving that.
 */

export interface NFLPlayerMetaProps {
  fullName: string;
  /**
   * SAB-1172: nfl_players.id. When provided alongside onOpenNews, a news badge renders
   * beside the name — but only if this player has ACTIVE news (playerNewsActive.ts).
   * Optional so every existing caller is unaffected.
   */
  playerId?: string | null;
  /** SAB-1172: open this player's card on the News tab. */
  onOpenNews?: () => void;
  /** Shorter form for narrow viewports. Falls back to fullName. */
  shortName?: string;
  position?: string | null;
  teamAlias?: string | null;
  /** nfl_franchises.bye_week_2026 — the NUMBER, for the "(5)" suffix. */
  byeWeek?: number | null;
  injuryStatus?: string | null;
  status?: string | null;
  /** This week's game. Null/absent scheduled means the player is on bye THIS week. */
  game?: GameContext | null;
  /** Kickoff has passed or the game left 'scheduled'. */
  isLocked?: boolean;
  /** No game this week. Rendered as a BYE chip instead of a game line. */
  isBye?: boolean;
  align?: 'left' | 'right';
  onClick?: () => void;
  className?: string;
  /**
   * False omits the meta line (position/team/game) so the caller can render
   * NFLPlayerMetaLine in a wider row. My Team does: inside its player cell the line got
   * 157px at >=xl against ~225px for the longest real line. Defaults to true, so the
   * Matchup rows and the overlays are unchanged.
   */
  metaLine?: boolean;
}

export function NFLPlayerMeta({
  fullName, shortName, position, teamAlias, byeWeek, playerId, onOpenNews,
  injuryStatus, status, game, isLocked = false, isBye = false,
  align = 'left', onClick, className, metaLine = true,
}: NFLPlayerMetaProps) {
  const right = align === 'right';

  return (
    <div
      className={cn('min-w-0', right && 'text-right', onClick && 'cursor-pointer', className)}
      onClick={onClick}
    >
      <div className={cn('flex items-center gap-1.5 min-w-0', right && 'flex-row-reverse')}>
        {shortName && (
          <span className="text-sm font-medium text-foreground truncate sm:hidden">{shortName}</span>
        )}
        <span className={cn(
          'text-sm font-medium text-foreground truncate',
          shortName ? 'hidden sm:inline' : '',
          onClick && 'hover:text-primary transition-colors',
        )}>
          {fullName}
        </span>
        <AvailabilityPill injuryStatus={injuryStatus} status={status} />
        {/* SAB-1172: shared by My Team and Matchup Detail, so both surfaces get the
            badge from one insertion. Renders nothing unless this player has active
            news AND the caller wired a handler. */}
        {playerId && onOpenNews && (
          <PlayerNewsIcon
            sport="nfl"
            playerId={playerId}
            playerName={fullName}
            onOpenNews={onOpenNews}
          />
        )}
      </div>

      {metaLine && (
        <NFLPlayerMetaLine
          position={position} teamAlias={teamAlias} byeWeek={byeWeek}
          game={game} isLocked={isLocked} isBye={isBye} align={align}
        />
      )}
    </div>
  );
}

export type NFLPlayerMetaLineProps = Pick<
  NFLPlayerMetaProps,
  'position' | 'teamAlias' | 'byeWeek' | 'game' | 'isLocked' | 'isBye' | 'align' | 'className'
> & {
  /**
   * True: a locked row KEEPS its kickoff + opponent and appends Locked ("… Sun 1:00 PM vs CIN
   * 🔒 Locked"), the Players page's game line with the lock added. False (default): Locked
   * replaces the game line -- kept for the Matchup cells, which are too narrow for both.
   * My Team passes true; it renders this line full-width, where both fit (measured).
   */
  lockedKeepsGame?: boolean;
};

/** The position/team/bye + game line under a player's name. NFLPlayerMeta renders it, or a
 *  caller with a wider row renders it on its own (see `metaLine`). */
export function NFLPlayerMetaLine({
  position, teamAlias, byeWeek, game, isLocked = false, isBye = false, align = 'left', className,
  lockedKeepsGame = false,
}: NFLPlayerMetaLineProps) {
  const right = align === 'right';
  const meta = playerMetaLine(position, teamAlias, byeWeek);
  const line = gameLine(game);
  const lockChip = (
    <span className="text-yellow-500 font-medium flex items-center gap-0.5 shrink-0" title="Locked — game started">
      <Lock className="h-2.5 w-2.5" />
      {/* APPEND mode on a phone: icon only (the word stays for screen readers and returns
          from sm). MEASURED at 390: the longest real line -- "COACH · DEN (10)" + "Mon 8:15 PM
          vs WAS" + the word -- needs 338px of 325 and wrapped; the word is ~38px of it. The
          yellow lock is the same mark the headshot overlay already uses. */}
      <span className={lockedKeepsGame ? 'sr-only sm:not-sr-only' : undefined}>Locked</span>
    </span>
  );

  return (
    <>
      {/* SAB-1015: WRAPS at narrow widths instead of crushing.
          Measured at 390x844 before this change: the "SAF · MIA (6)" span was squeezed to
          clientWidth 3px against a 69px scrollWidth, and "RB · ATL (11)" to 1px against
          64px -- the meta line was there but invisible, which is the "names clipped
          mid-word" in the report. Every child was shrinkable and none had a floor, so flex
          took the space from all of them at once.
          Wrapping gives the game line its own row when it cannot sit beside the
          position/team, which is the ticket's "meta line allowed to wrap or abbreviate". */}
      <div className={cn(
        // SAB-1015: wraps on PHONES only. From sm up there is room for one line, and the
        // single-line form is what desktop had before any of this -- wrapping there would be
        // a regression introduced by the mobile fix.
        'flex flex-wrap sm:flex-nowrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-muted-foreground min-w-0',
        right && 'flex-row-reverse',
        className,
      )}>
        {/* basis-auto + a min width so this never collapses to a sliver; it is the one
            part of the line that identifies the player's position and team. */}
        {/* min-w-0: a flex child will not shrink below its content width without it, so
            `truncate` alone would be inert. */}
        {meta && <span className="font-medium truncate min-w-0">{meta}</span>}

        {/* On a bye there is no game line to show, so the chip replaces it rather than
            sitting beside an empty space. */}
        {isBye && (
          <span className="text-[9px] font-bold text-amber-400 bg-amber-400/10 px-1 rounded shrink-0">
            BYE
          </span>
        )}

        {/* SUBSTITUTE mode (the default): Locked stands in for the game line. */}
        {!isBye && isLocked && !lockedKeepsGame && lockChip}

        {/* The game line. In SUBSTITUTE mode it is hidden once locked. In APPEND mode
            (`lockedKeepsGame`) it stays: the opponent is still true after kickoff, and a
            slate is exactly when a manager is reading these rows -- substituting blanked the
            opponent on every row at once, the state users see most of the week. */}
        {!isBye && line && (!isLocked || lockedKeepsGame) && (
          <span className="flex items-center gap-0.5 min-w-0 truncate">
            <Clock className="h-2.5 w-2.5 shrink-0" />
            <span className="truncate">{line}</span>
          </span>
        )}

        {/* APPEND mode: the lock rides after the game line, not instead of it. */}
        {!isBye && isLocked && lockedKeepsGame && lockChip}

      </div>
    </>
  );
}

export default NFLPlayerMeta;
